import {
  computeCategoryActive,
  computeFamilyActive,
  isLadType,
  isPerimType,
  toggleType,
  updateTypeMap,
  updateTypes,
} from '@components/cockpit/panels/monitoring/monitoring.utils';
import { MonitoringFilter, MonitoringState, SortEnum } from '@components/cockpit/panels/monitoring/MonitoringContext';
import { toggleMapValue } from '@utils/common.utils';
import { CategoryEnum } from '@utils/sensors/configuration.constants';
import { useCallback, useReducer } from 'react';

import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

import {
  categoryFromSensorFamily,
  CriticalityCategoryEnum,
  MonitoringSensorType,
  sensorFamilyFromMonitoringSensorType,
} from './monitoring.types';

export type Action =
  | { type: 'TOGGLE_CATEGORY'; payload: CategoryEnum }
  | { type: 'TOGGLE_FAMILY'; payload: SensorFamilyEnum }
  | { type: 'TOGGLE_MONITORING_TYPE'; payload: MonitoringSensorType }
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_STATUS'; payload: Set<CriticalityCategoryEnum> }
  | { type: 'SET_SHOW_PARENT'; payload: boolean }
  | { type: 'SET_SHOW_CHILD'; payload: boolean }
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'SET_SELECTED_SENSOR_KEY'; payload: string | null }
  | { type: 'SET_SORT'; payload: SortEnum }
  | { type: 'RESET'; payload?: MonitoringFilter }
  | { type: 'SYNC_FILTERS'; payload: MonitoringFilter };

function mergeFilterMap<K>(current: Map<K, boolean>, available: Map<K, boolean>): Map<K, boolean> {
  const result = new Map(available);

  for (const [key, value] of current) {
    if (result.has(key)) {
      result.set(key, value);
    }
  }

  return result;
}

function monitoringReducer(state: MonitoringState, action: Action): MonitoringState {
  switch (action.type) {
    case 'TOGGLE_CATEGORY': {
      const { payload: category } = action;
      const newCategories = toggleMapValue(state.categories, category);
      const isActive = newCategories.get(category) ?? false;

      const newFamilies = new Map(state.families);
      for (const [family, cat] of Object.entries(categoryFromSensorFamily)) {
        if (cat === category) {
          const hasType = [...state.ladTypes, ...state.perimTypes].some(
            ([type]) => sensorFamilyFromMonitoringSensorType[type] === family,
          );
          if (hasType) {
            newFamilies.set(family as SensorFamilyEnum, isActive);
          }
        }
      }

      const newLadTypes = updateTypeMap(state.ladTypes, category === CategoryEnum.LAD, isActive);
      const newPerimTypes = updateTypeMap(state.perimTypes, category === CategoryEnum.PERIM, isActive);

      return {
        ...state,
        categories: newCategories,
        families: newFamilies,
        ladTypes: newLadTypes,
        perimTypes: newPerimTypes,
      };
    }
    case 'TOGGLE_FAMILY': {
      const { payload: family } = action;
      const newFamilies = toggleMapValue(state.families, family);
      const category = categoryFromSensorFamily[family];
      const isActive = newFamilies.get(family) ?? false;

      const newLadTypes = updateTypes(state.ladTypes, family, isActive);
      const newPerimTypes = updateTypes(state.perimTypes, family, isActive);

      const isCategoryActive = computeCategoryActive(newFamilies, category);
      const newCategories = new Map(state.categories).set(category, isCategoryActive);

      return {
        ...state,
        categories: newCategories,
        families: newFamilies,
        ladTypes: newLadTypes,
        perimTypes: newPerimTypes,
      };
    }
    case 'TOGGLE_MONITORING_TYPE': {
      const { payload: type } = action;
      const newLadTypes = isLadType(type) ? toggleType(state.ladTypes, type) : state.ladTypes;
      const newPerimTypes = isPerimType(type) ? toggleType(state.perimTypes, type) : state.perimTypes;

      const family = sensorFamilyFromMonitoringSensorType[type];
      const isFamilyActive = computeFamilyActive(newLadTypes, newPerimTypes, family);
      const newFamilies = new Map(state.families).set(family, isFamilyActive);

      const category = categoryFromSensorFamily[family];
      const isCategoryActive = computeCategoryActive(newFamilies, category);
      const newCategories = new Map(state.categories).set(category, isCategoryActive);

      return {
        ...state,
        categories: newCategories,
        families: newFamilies,
        ladTypes: newLadTypes,
        perimTypes: newPerimTypes,
      };
    }
    case 'SET_NAME':
      return { ...state, name: action.payload };
    case 'SET_STATUS':
      return { ...state, statusCategories: new Set(action.payload) };
    case 'SET_SHOW_PARENT':
      return { ...state, showParent: action.payload };
    case 'SET_SHOW_CHILD':
      return { ...state, showChild: action.payload };
    case 'SET_OPEN':
      return { ...state, open: action.payload };
    case 'SET_SELECTED_SENSOR_KEY':
      return { ...state, selectedSensorKey: action.payload };
    case 'SET_SORT':
      return { ...state, sort: action.payload };
    case 'RESET':
      return {
        ...state,
        ...(action.payload ?? {}),
        categories: new Map(action.payload?.categories ?? state.categories),
        families: new Map(action.payload?.families ?? state.families),
        ladTypes: new Map(action.payload?.ladTypes ?? state.ladTypes),
        perimTypes: new Map(action.payload?.perimTypes ?? state.perimTypes),
        statusCategories: new Set(action.payload?.statusCategories ?? state.statusCategories),
      };
    case 'SYNC_FILTERS':
      return {
        ...state,
        categories: mergeFilterMap(state.categories, action.payload.categories),
        families: mergeFilterMap(state.families, action.payload.families),
        ladTypes: mergeFilterMap(state.ladTypes, action.payload.ladTypes),
        perimTypes: mergeFilterMap(state.perimTypes, action.payload.perimTypes),
      };
  }
}

export type UseMonitoringReducerReturn = {
  state: MonitoringState;
  toggleCategory: (category: CategoryEnum) => void;
  toggleFamily: (family: SensorFamilyEnum) => void;
  toggleMonitoringType: (type: MonitoringSensorType) => void;
  setName: (value: string) => void;
  setStatus: (statuses: Set<CriticalityCategoryEnum>) => void;
  setShowParent: (showParent: boolean) => void;
  setShowChild: (showChild: boolean) => void;
  setOpen: (open: boolean) => void;
  setSelectedSensorKey: (selectedSensorKey: string | null) => void;
  setSort: (sort: SortEnum) => void;
  reset: () => void;
  syncFilters: (filters: MonitoringFilter) => void;
};

export const useMonitoringReducer = (
  initialState: MonitoringState,
  defaultFilters: MonitoringFilter,
): UseMonitoringReducerReturn => {
  const [state, dispatch] = useReducer(monitoringReducer, initialState);

  const action = useCallback((action: Action) => dispatch(action), []);

  const syncFilters = useCallback(
    (filters: MonitoringFilter) => {
      action({ type: 'SYNC_FILTERS', payload: filters });
    },
    [action],
  );

  return {
    state,
    toggleCategory: (category: CategoryEnum) => action({ type: 'TOGGLE_CATEGORY', payload: category }),
    toggleFamily: (family: SensorFamilyEnum) => action({ type: 'TOGGLE_FAMILY', payload: family }),
    toggleMonitoringType: (type: MonitoringSensorType) => action({ type: 'TOGGLE_MONITORING_TYPE', payload: type }),
    setName: (name: string) => action({ type: 'SET_NAME', payload: name }),
    setStatus: (statuses: Set<CriticalityCategoryEnum>) => action({ type: 'SET_STATUS', payload: statuses }),
    setShowParent: (showParent: boolean) => action({ type: 'SET_SHOW_PARENT', payload: showParent }),
    setShowChild: (showChild: boolean) => action({ type: 'SET_SHOW_CHILD', payload: showChild }),
    setOpen: (open: boolean) => action({ type: 'SET_OPEN', payload: open }),
    setSelectedSensorKey: (selectedSensorKey: string | null) =>
      action({ type: 'SET_SELECTED_SENSOR_KEY', payload: selectedSensorKey }),
    setSort: (sort: SortEnum) => action({ type: 'SET_SORT', payload: sort }),
    reset: () => action({ type: 'RESET', payload: defaultFilters }),
    syncFilters,
  };
};
